import { c as _c } from "react/compiler-runtime";
import AppConfig from '@/config';
import { cn } from '@/lib/utils';
import { Loader2, Mic, Pause, Play } from '@/components/ui';
import { useI18n } from '@/context/I18nProvider';
import { useCallback, useEffect, useRef, useState } from 'react';
type VoiceMessagePlayerProps = {
  /** Relative audio URL from the API (e.g., "/public/voice-message/{id}") */
  audioUrl: string;
  /** The transcribed text content */
  transcription: string;
  /** Whether this is an outbound (sent) message */
  isOutbound: boolean;
};
function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
export const VoiceMessagePlayer = t0 => {
  const $ = _c(51);
  const {
    audioUrl,
    transcription,
    isOutbound
  } = t0;
  const {
    t
  } = useI18n();
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [showTranscription, setShowTranscription] = useState(false);
  const fullUrl = `${AppConfig.endpoints.client.base}${audioUrl}`;
  let t1;
  let t2;
  if ($[0] !== fullUrl) {
    t1 = () => {
      const audio = new Audio(fullUrl);
      audioRef.current = audio;
      audio.addEventListener("loadedmetadata", () => {
        setDuration(audio.duration);
      });
      audio.addEventListener("timeupdate", () => {
        setCurrentTime(audio.currentTime);
      });
      audio.addEventListener("ended", () => {
        setIsPlaying(false);
        setCurrentTime(0);
      });
      audio.addEventListener("waiting", () => setIsLoading(true));
      audio.addEventListener("canplay", () => setIsLoading(false));
      return () => {
        audio.pause();
        audio.src = "";
      };
    };
    t2 = [fullUrl];
    $[0] = fullUrl;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== isPlaying) {
    t3 = () => {
      const audio_0 = audioRef.current;
      if (!audio_0) {
        return;
      }
      if (isPlaying) {
        audio_0.pause();
        setIsPlaying(false);
      } else {
        setIsLoading(true);
        audio_0.play().then(() => {
          setIsPlaying(true);
          setIsLoading(false);
        }).catch(() => {
          setIsLoading(false);
        });
      }
    };
    $[3] = isPlaying;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const togglePlay = t3;
  const progress = duration > 0 ? currentTime / duration * 100 : 0;
  let t4;
  if ($[5] !== transcription) {
    let t5;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = /^\[Message vocal\]\s*/;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    t4 = transcription.replace(t5, "");
    $[5] = transcription;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const cleanTranscription = t4;
  const t5 = isOutbound ? "bg-primary text-primary-foreground" : "bg-secondary text-text-primary";
  let t6;
  if ($[8] !== t5) {
    t6 = cn("flex items-center gap-2.5 rounded-2xl px-3.5 py-2.5 min-w-[200px]", t5);
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = isOutbound ? "bg-primary-foreground/20 hover:bg-primary-foreground/30" : "bg-primary/10 hover:bg-primary/20";
  let t8;
  if ($[10] !== t7) {
    t8 = cn("flex items-center justify-center size-8 rounded-full shrink-0 transition-colors", t7);
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== isLoading || $[13] !== isPlaying) {
    t9 = isLoading ? <Loader2 className="size-4 animate-spin" /> : isPlaying ? <Pause className="size-4" /> : <Play className="size-4 ml-0.5" />;
    $[12] = isLoading;
    $[13] = isPlaying;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t8 || $[16] !== t9 || $[17] !== togglePlay) {
    t10 = <button onClick={togglePlay} className={t8}>{t9}</button>;
    $[15] = t8;
    $[16] = t9;
    $[17] = togglePlay;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const t11 = isOutbound ? "bg-primary-foreground/20" : "bg-primary/10";
  let t12;
  if ($[19] !== t11) {
    t12 = cn("h-1.5 rounded-full overflow-hidden", t11);
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const t13 = isOutbound ? "bg-primary-foreground/60" : "bg-primary/40";
  let t14;
  if ($[21] !== t13) {
    t14 = cn("h-full rounded-full transition-all duration-100", t13);
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const t15 = `${progress}%`;
  let t16;
  if ($[23] !== t15) {
    t16 = {
      width: t15
    };
    $[23] = t15;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== t14 || $[26] !== t16) {
    t17 = <div className={t14} style={t16} />;
    $[25] = t14;
    $[26] = t16;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== t12 || $[29] !== t17) {
    t18 = <div className="flex-1 flex flex-col gap-1"><div className={t12}>{t17}</div></div>;
    $[28] = t12;
    $[29] = t17;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== currentTime || $[32] !== duration || $[33] !== isPlaying) {
    t19 = isPlaying || currentTime > 0 ? formatDuration(currentTime) : duration > 0 ? formatDuration(duration) : "0:00";
    $[31] = currentTime;
    $[32] = duration;
    $[33] = isPlaying;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== t19) {
    t20 = <span className="text-[11px] font-medium tabular-nums shrink-0 opacity-70">{t19}</span>;
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Mic className="size-3.5 shrink-0 opacity-50" />;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== t10 || $[39] !== t18 || $[40] !== t20 || $[41] !== t6) {
    t22 = <div className={t6}>{t10}{t18}{t20}{t21}</div>;
    $[38] = t10;
    $[39] = t18;
    $[40] = t20;
    $[41] = t6;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== cleanTranscription || $[44] !== isOutbound || $[45] !== showTranscription || $[46] !== t) {
    t23 = cleanTranscription && <div className={cn("px-1", isOutbound ? "self-end" : "self-start")}><button onClick={() => setShowTranscription(!showTranscription)} className="text-[11px] text-muted-foreground hover:text-text-primary transition-colors">{showTranscription ? t("conversations.hideTranscription") : t("conversations.viewTranscription")}</button>{showTranscription && <p className={cn("text-[12px] leading-[16px] mt-1 max-w-[300px]", isOutbound ? "text-right text-muted-foreground" : "text-muted-foreground")}>{cleanTranscription}</p>}</div>;
    $[43] = cleanTranscription;
    $[44] = isOutbound;
    $[45] = showTranscription;
    $[46] = t;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== t22 || $[49] !== t23) {
    t24 = <div className="flex flex-col gap-1">{t22}{t23}</div>;
    $[48] = t22;
    $[49] = t23;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  return t24;
};